import { useState, useEffect, useCallback } from 'react';
import { getPlanCodesForCompany } from '../planDetails';

/**
 * Custom hook to manage company profile data fetching
 * @param {string} companyId - The company ID to fetch data for
 * @returns {Object} Company data state and helpers
 */
export const useCompanyData = (companyId) => {
  const [corporateProfile, setCorporateProfile] = useState(null);
  const [officeProfiles, setOfficeProfiles] = useState([]);
  const [companyPlans, setCompanyPlans] = useState([]);
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);
  const [companyDataError, setCompanyDataError] = useState(null);

  const fetchCompanyData = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setIsLoadingCompanyData(true);
    setCompanyDataError(null);

    try {
      // Fetch corporate profile with office-profiles
      const response = await fetch(
        `${process.env.REACT_APP_MIKASA_URL}/corporate-profiles/${companyId}?include=office-profiles`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            'Accept': 'application/vnd.api+json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch company data: ${response.status}`);
      }

      const responseData = await response.json();

      // Set corporate profile data
      if (responseData.data) {
        setCorporateProfile(responseData.data);

        // Extract plan codes from the corporate profile mapping
        const planCodes = getPlanCodesForCompany(companyId);

        // Fetch plans separately for each plan code
        if (planCodes && planCodes.length > 0) {
          // Fetch all plans in parallel
          const planPromises = planCodes.map(planCode =>
            fetch(`${process.env.REACT_APP_MIKASA_URL}/plans/signup/${planCode}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/vnd.api+json',
                'Accept': 'application/vnd.api+json'
              }
            }).then(res => res.ok ? res.json() : null)
          );

          const planResponses = await Promise.all(planPromises);
          // Flatten and filter out null responses, then extract data arrays
          const allPlans = planResponses
            .filter(response => response && response.data)
            .flatMap(response => Array.isArray(response.data) ? response.data : [response.data]);

          setCompanyPlans(allPlans);
        }
      }

      // Set office profiles data
      if (responseData.included) {
        const offices = responseData.included.filter(
          item => item.type === 'office-profiles'
        );
        setOfficeProfiles(offices);
      }

    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyDataError(error.message);
    } finally {
      setIsLoadingCompanyData(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  return {
    corporateProfile,
    officeProfiles,
    companyPlans,
    isLoadingCompanyData,
    companyDataError,
    refetchCompanyData: fetchCompanyData
  };
};

export default useCompanyData;
